/**
 * Every query key, in one place.
 *
 * AGENTS.md §15a: keys come from a typed factory and always include
 * `workspaceId`. A cached response must not survive a workspace switch — which
 * is exactly what happens when a key is written by hand at the call site and
 * someone forgets the tenant. The cache would then show one customer another
 * customer's numbers, and no server-side check would ever see the request.
 */
interface OffPageKey {
  readonly from: string;
  readonly to: string;
  readonly engine?: string;
  readonly limit: number;
  readonly offset: number;
}

function offPageKey(query: OffPageKey) {
  return [
    query.from,
    query.to,
    query.engine ?? "all",
    query.limit,
    query.offset,
  ] as const;
}

/** The operator panel's pages, each one a key namespace of its own. */
export type AdminPage =
  | "overview"
  | "spend"
  | "capture"
  | "customers"
  | "customer"
  | "onboarding"
  | "services"
  | "engines"
  | "model-usage"
  | "jobs"
  | "emails"
  | "storage"
  | "audit"
  | "usage"
  | "usage-feature"
  | "usage-compare"
  | "ai-apps"
  | "customer-ai-apps"
  | "access-requests"
  | "accounts"
  | "account"
  | "me";

/** One opportunity's answers, as the lens and the insight are both asked for it. */
interface LensKey {
  readonly from: string;
  readonly to: string;
  readonly engine?: string;
  readonly by: string;
  readonly value: string;
  readonly otherBrand?: string;
  readonly otherEntity?: string;
}

export const queryKeys = {
  /**
   * The operator panel. **Not workspace-scoped, on purpose:** it is the one
   * place in the app that reads every customer, so a `workspaceId` in the key
   * would be false. What keeps one customer's cache from reaching another's is
   * that nothing here is ever shown to a customer, and that sign-out clears the
   * whole cache (app.tsx). Anything that varies the answer (range, filters,
   * page) is still in the key, per the rule above.
   */
  admin: (page: AdminPage, ...parts: readonly (string | number)[]) =>
    ["admin", page, ...parts] as const,
  me: () => ["me"] as const,
  /** Your own account. Not a workspace's, so, like `me`, it carries none. */
  account: () => ["account"] as const,
  /**
   * What an invite link opens. Read before its holder belongs to any
   * workspace, so it is keyed by the link, like `me` is keyed by nothing.
   */
  invite: (token: string) => ["invite", token] as const,
  /** Opening that link while signed in, as this person. */
  inviteAcceptance: (token: string, userId: string) =>
    ["invite", token, "accepted-by", userId] as const,
  members: (workspaceId: string) => ["members", workspaceId] as const,
  people: (workspaceId: string) => ["people", workspaceId] as const,
  myAccess: (workspaceId: string) => ["people", workspaceId, "me"] as const,
  apiKeys: (workspaceId: string) => ["api-keys", workspaceId] as const,
  /** Your own AI app connections, in every workspace: yours, so no workspace. */
  connections: () => ["account", "connections"] as const,
  /**
   * The consent screen's view of an app's ask. Before any workspace is
   * chosen, so keyed by the ask itself; it holds no workspace's data.
   */
  oauthConsent: (search: string) => ["oauth-consent", search] as const,
  /** The workspace's grant, and how full it is. */
  subscription: (workspaceId: string) => ["subscription", workspaceId] as const,
  /** What Crux sells. The same for everyone, before anyone signs in. */
  planCatalog: () => ["plans"] as const,
  runs: (workspaceId: string) => ["runs", workspaceId] as const,
  brands: (workspaceId: string) => ["brands", workspaceId] as const,
  brandCoverage: (workspaceId: string, brandId: string) =>
    ["brand-coverage", workspaceId, brandId] as const,
  reports: (workspaceId: string) => ["reports", workspaceId] as const,
  /** The signed-in person's own email switches for one report. */
  emailPreferences: (workspaceId: string, reportId: string) =>
    ["email-preferences", workspaceId, reportId] as const,
  /** The emails the signed-in person was sent about one report. */
  emailHistory: (workspaceId: string, reportId: string) =>
    ["email-history", workspaceId, reportId] as const,
  /** The rivals one report's answers keep naming that nobody tracks. */
  discoveredCompanies: (workspaceId: string, reportId: string) =>
    ["discovered-companies", workspaceId, reportId] as const,
  /** Names that look like a brand already tracked, awaiting approval. */
  aliasCandidates: (workspaceId: string) => ["alias-candidates", workspaceId] as const,
  promptLibrary: (workspaceId: string) => ["prompt-library", workspaceId] as const,
  fanoutStudies: (workspaceId: string) => ["fanout-studies", workspaceId] as const,
  fanoutStudy: (workspaceId: string, studyId: string) =>
    ["fanout-study", workspaceId, studyId] as const,
  pageStudies: (workspaceId: string) => ["page-studies", workspaceId] as const,
  pageStudy: (workspaceId: string, studyId: string) =>
    ["page-study", workspaceId, studyId] as const,
  /** Every page's hits in a workspace — the prefix "Add to brand" refreshes. */
  pageHitsAll: (workspaceId: string) => ["page-hits", workspaceId] as const,
  pageHits: (workspaceId: string, studyId: string, pageId: string) =>
    ["page-hits", workspaceId, studyId, pageId] as const,
  /**
   * The filters are part of the key, as the raw querystring.
   *
   * Two different filters are two different answers, and a key that ignored
   * them would serve the previous filter's rows under the new filter's heading
   * — which reads as data rather than as a stale cache.
   */
  responses: (workspaceId: string, search: string) =>
    ["responses", workspaceId, search] as const,
  responseDetail: (workspaceId: string, runId: string) =>
    ["response-detail", workspaceId, runId] as const,
  overview: (workspaceId: string, reportId: string, from: string, to: string) =>
    ["overview", workspaceId, reportId, from, to] as const,
  /** One part's whole list of sites: the range, the engine and the part all decide it. */
  domainSourceSites: (
    workspaceId: string,
    reportId: string,
    query: {
      readonly from: string;
      readonly to: string;
      readonly engine?: string;
      readonly group: string;
    },
  ) =>
    [
      "domain-source-sites",
      workspaceId,
      reportId,
      query.from,
      query.to,
      query.engine ?? "all",
      query.group,
    ] as const,
  /**
   * One page of one Sources table. The range and engine come first, so a
   * page change can tell it is the same range (`useSources`); then every key
   * that changes the rows: tab, search, part, list, order and page.
   */
  sources: (
    workspaceId: string,
    reportId: string,
    query: {
      readonly from: string;
      readonly to: string;
      readonly engine?: string;
      readonly tab?: string;
      readonly q?: string;
      readonly kind?: string;
      readonly list?: string;
      readonly sort?: string;
      readonly order?: string;
      readonly offset?: number;
    },
  ) =>
    [
      "sources",
      workspaceId,
      reportId,
      query.from,
      query.to,
      query.engine ?? "all",
      query.tab ?? "sites",
      query.q ?? "",
      query.kind ?? "all",
      query.list ?? "cited",
      query.sort ?? "",
      query.order ?? "",
      query.offset ?? 0,
    ] as const,
  /** Opportunities over a range and engine (DESIGN.md §9, "Opportunities"). */
  opportunities: (
    workspaceId: string,
    reportId: string,
    query: { readonly from: string; readonly to: string; readonly engine?: string },
  ) =>
    [
      "opportunities",
      workspaceId,
      reportId,
      query.from,
      query.to,
      query.engine ?? "all",
    ] as const,
  /** One opportunity opened: every argument that picks its answers is in the key. */
  opportunityLens: (workspaceId: string, reportId: string, query: LensKey) =>
    [
      "opportunity-lens",
      workspaceId,
      reportId,
      query.from,
      query.to,
      query.engine ?? "all",
      query.by,
      query.value,
      query.otherBrand ?? "",
      query.otherEntity ?? "",
    ] as const,
  /** One opportunity looked into: the same answers as its lens, so the same key parts. */
  opportunityInsight: (workspaceId: string, reportId: string, query: LensKey) =>
    [
      "opportunity-insight",
      workspaceId,
      reportId,
      query.from,
      query.to,
      query.engine ?? "all",
      query.by,
      query.value,
      query.otherBrand ?? "",
      query.otherEntity ?? "",
    ] as const,
  /** The readings of a set of pages, which only the pages and the range pick. */
  pageReadings: (
    workspaceId: string,
    reportId: string,
    ask: {
      readonly from: string;
      readonly to: string;
      readonly engine?: string;
      readonly urls: readonly string[];
    },
  ) =>
    [
      "page-readings",
      workspaceId,
      reportId,
      ask.from,
      ask.to,
      ask.engine ?? "all",
      ...ask.urls,
    ] as const,
  /** No range: robots.txt is about the site today, not a window of answers. */
  aiAccess: (workspaceId: string, reportId: string) =>
    ["ai-access", workspaceId, reportId] as const,
  /** One brand's Google Analytics connection, and what it can see. */
  analyticsConnection: (workspaceId: string, brandId: string) =>
    ["analytics-connection", workspaceId, brandId] as const,
  analyticsProperties: (workspaceId: string, brandId: string) =>
    ["analytics-properties", workspaceId, brandId] as const,
  aiVisits: (workspaceId: string, reportId: string, from: string, to: string) =>
    ["ai-visits", workspaceId, reportId, from, to] as const,
  /**
   * No date range in the key, because the endpoint takes none.
   *
   * Progress is about the most recent batch, not about a window someone chose.
   * Keying it by range would cache one answer per range and show a stale count
   * the moment a person widened the dates.
   */
  captureProgress: (workspaceId: string, reportId: string) =>
    ["capture-progress", workspaceId, reportId] as const,
  /*
   * Off-page reads. Every argument that changes the answer is in the key —
   * range, engine, paging, and the domain, URL or answer filter — so a page of
   * one site can never be read back from the cache as a page of another.
   */
  offPageSites: (
    workspaceId: string,
    reportId: string,
    sites: string,
    query: OffPageKey & {
      readonly q?: string;
      readonly category?: string;
      readonly inOms?: string;
      readonly minDa?: number;
      readonly minTraffic?: number;
      readonly maxSpam?: number;
      readonly maxPrice?: number;
      readonly sort?: string;
      readonly dir?: string;
    },
  ) =>
    [
      "off-page-sites",
      workspaceId,
      reportId,
      sites,
      query.q ?? "",
      query.category ?? "any",
      // OMS's bounds and order change the answer, so they are the key too.
      [
        query.inOms ?? "",
        query.minDa ?? "",
        query.minTraffic ?? "",
        query.maxSpam ?? "",
        query.maxPrice ?? "",
        query.sort ?? "gap",
        query.dir ?? "",
      ].join("|"),
      ...offPageKey(query),
    ] as const,
  /** Prefixes, for refetching every cached range after a category changes. */
  offPageSitesFor: (workspaceId: string, reportId: string) =>
    ["off-page-sites", workspaceId, reportId] as const,
  offPageSiteFor: (workspaceId: string, reportId: string) =>
    ["off-page-site", workspaceId, reportId] as const,
  offPagePageFor: (workspaceId: string, reportId: string) =>
    ["off-page-page", workspaceId, reportId] as const,
  offPageSiteAnswers: (
    workspaceId: string,
    reportId: string,
    site: { readonly domain: string; readonly show: string },
    query: OffPageKey,
  ) =>
    [
      "off-page-site-answers",
      workspaceId,
      reportId,
      site.domain,
      site.show,
      ...offPageKey(query),
    ] as const,
  offPageSite: (
    workspaceId: string,
    reportId: string,
    domain: string,
    query: OffPageKey,
  ) => ["off-page-site", workspaceId, reportId, domain, ...offPageKey(query)] as const,
  offPagePage: (
    workspaceId: string,
    reportId: string,
    page: { readonly url: string; readonly show: string },
    query: OffPageKey,
  ) =>
    [
      "off-page-page",
      workspaceId,
      reportId,
      page.url,
      page.show,
      ...offPageKey(query),
    ] as const,
  /*
   * OMS's answer is the same for every workspace, but the key still starts
   * with one (apps/web/AGENTS.md): a key without it is the habit that leaks.
   * Sorted, so the same sites in another order are the same question.
   */
  omsListings: (workspaceId: string, domains: readonly string[]) =>
    ["oms-listings", workspaceId, ...[...domains].sort()] as const,
} as const;
